import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, BASE, getAuthHeaders } from '@/api/client'

// ─── Types ───

export interface DubbingJob {
  dubbing_id: string
  name: string
  status: 'dubbing' | 'dubbed' | 'error'
  target_lang: string
  source_lang?: string
  expected_duration_sec?: number
  error?: string
  created_at?: string
}

export interface DubbingListResponse {
  dubbing: DubbingJob[]
}

export interface CreateDubbingResponse {
  dubbing_id: string
  expected_duration_sec: number
}

export interface ElevenLabsKeyInfo {
  hasKey: boolean
  maskedKey: string | null
}

// ─── API Key Hooks ───

export function useElevenLabsKey() {
  return useQuery<ElevenLabsKeyInfo>({
    queryKey: ['elevenlabs', 'api-key'],
    queryFn: () => api.get<ElevenLabsKeyInfo>('/elevenlabs/api-key'),
  })
}

export function useSaveElevenLabsKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (apiKey: string) => api.post<{ ok: boolean; maskedKey: string }>('/elevenlabs/api-key', { apiKey }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['elevenlabs', 'api-key'] })
    },
  })
}

export function useDeleteElevenLabsKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<{ ok: boolean }>('/elevenlabs/api-key'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['elevenlabs', 'api-key'] })
    },
  })
}

// ─── Dubbing Hooks ───

export function useCreateDubbing() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: {
      sourceUrl?: string
      file?: File
      targetLang: string
      sourceLang?: string
      name?: string
    }): Promise<CreateDubbingResponse> => {
      const fd = new FormData()

      if (params.file) {
        fd.append('file', params.file)
      } else if (params.sourceUrl) {
        fd.append('sourceUrl', params.sourceUrl)
      }

      fd.append('targetLang', params.targetLang)
      if (params.sourceLang && params.sourceLang !== 'auto') {
        fd.append('sourceLang', params.sourceLang)
      }
      if (params.name) fd.append('name', params.name)

      const res = await fetch(`${BASE}/elevenlabs/dubbing`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          ...getAuthHeaders(),
        },
        body: fd,
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Erro ao criar dublagem' }))
        throw new Error(body.error || `HTTP ${res.status}`)
      }

      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['elevenlabs', 'dubbing'] })
    },
  })
}

export function useDubbingList() {
  return useQuery<DubbingJob[]>({
    queryKey: ['elevenlabs', 'dubbing'],
    queryFn: async () => {
      const data = await api.get<DubbingListResponse>('/elevenlabs/dubbing')
      return data.dubbing ?? []
    },
    refetchInterval: 10000,
  })
}

export function useDubbingStatus(id: string | null) {
  return useQuery<DubbingJob>({
    queryKey: ['elevenlabs', 'dubbing', id],
    queryFn: () => api.get<DubbingJob>(`/elevenlabs/dubbing/${id}`),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'dubbing') return 5000
      return false
    },
  })
}

export function useDeleteDubbing() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/elevenlabs/dubbing/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['elevenlabs', 'dubbing'] })
    },
  })
}

export function getDubbingDownloadUrl(id: string, lang: string): string {
  return `${BASE}/elevenlabs/dubbing/${id}/download/${lang}`
}

export async function downloadDubbedVideo(id: string, lang: string, filename?: string): Promise<void> {
  const url = getDubbingDownloadUrl(id, lang)
  const res = await fetch(url, {
    headers: { ...getAuthHeaders(), Accept: 'application/octet-stream' },
  })
  if (!res.ok) {
    let msg = `Download failed: HTTP ${res.status}`
    try {
      const json = await res.json()
      if (json.error) msg = json.error
      else if (json.message) msg = json.message
    } catch { /* not JSON, keep default msg */ }
    throw new Error(msg)
  }
  const blob = await res.blob()
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename || `dubbed_${lang}.mp4`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(a.href)
}
